import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "d-flex ga-2" }

import { computed, ref, watch } from "vue";
import { v4 } from "uuid";

import type { MappingBackends, RectangleSelection, TimeRange, UserDataset } from "../types";
import type { MillisecondRange } from "../types/datetime";
import { type MoleculeType, MOLECULE_OPTIONS } from "../esri/utils";

interface SelectionComposerProps {
  backend: MappingBackends;
  timeRanges: TimeRange[];
  regions: RectangleSelectionType[];
  disabled?: { region?: boolean; timeRange?: boolean; molecule?: boolean };
}

type RectangleSelectionType = RectangleSelection;

interface DraftUserDataset {
  region?: RectangleSelectionType | null;
  timeRange?: TimeRange | null;
  molecule?: MoleculeType | null;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'SelectionComposer',
  props: {
    backend: {},
    timeRanges: {},
    regions: {},
    disabled: {}
  },
  emits: ["create"],
  setup(__props: any, { emit: __emit }) {

const selectedTimeRange = ref<TimeRange | null>(null);

const props = __props;

const emit = __emit;

const loading = ref(false);

const draftUserDataset = ref<DraftUserDataset>({ region: null, timeRange: null, molecule: null });
const availableMolecules = computed(() => MOLECULE_OPTIONS.map(o => ({ key: o.value as MoleculeType, title: o.title })));

// Public composition progress
const creationProgress = computed(() => {
  let count = 0;
  if (draftUserDataset.value.region) count += 1;
  if (draftUserDataset.value.timeRange) count += 1;
  if (draftUserDataset.value.molecule) count += 1;
  return { count, percent: (count / 3) * 100 };
});

function setDraftSelectionRegion(region: RectangleSelectionType | null) {
  (draftUserDataset.value.region as RectangleSelectionType | null) = region || null;
}

function setDraftSelectionMolecule(molecule: MoleculeType | null) {
  draftUserDataset.value.molecule = molecule || null;
}

function setDraftSelectionTimeRange(range: TimeRange | null) {
  draftUserDataset.value.timeRange = range || null;
}

async function composeSelection(): Promise<UserDataset | null> {
  // const color = COLORS[selectionCount % COLORS.length]; // we will use the color from the region
  
  const draft = draftUserDataset.value;
  if (!draft.region || !draft.timeRange || !draft.molecule) {
    console.error('Draft selection incomplete');
    return null;
  }
  
  const sel: UserDataset = {
    id: v4(),
    region: draft.region, // as { id: string; name: string; rectangle: RectangleSelectionInfo; color: string; layer?: unknown },
    timeRange: draft.timeRange,
    molecule: draft.molecule as MoleculeType,
  };
  emit("create", sel);
  return sel;
}


function reset() {
  draftUserDataset.value = { region: null, timeRange: null, molecule: null };
  selectedTimeRange.value = null;
}

watch(selectedTimeRange, (timeRange: TimeRange | null) => {
  setDraftSelectionTimeRange(timeRange ?? null);
});

// just watch the id's. using a 'deep' watch caused a significant performance hit
watch(() => props.regions.map(r => r.id), (newRegions) => {
  if (draftUserDataset.value.region) {
    const exists = newRegions.find(rid => rid === draftUserDataset.value.region?.id);
    if (!exists) {
      draftUserDataset.value.region = null;
    }
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item_subtitle = _resolveComponent("v-list-item-subtitle")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(_component_v_card, { id: "selection-composer" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, { class: "d-flex justify-space-between" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_chip, {
            size: "x-small",
            color: "primary",
            variant: "tonal"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(creationProgress.value.count) + "/3", 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_card_text, { class: "d-flex flex-column ga-3" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_select, {
            items: __props.regions.map(r => ({ title: r.name, value: r })),
            "model-value": draftUserDataset.value.region,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (setDraftSelectionRegion($event as RectangleSelectionType))),
            label: "Region",
            disabled: __props.disabled?.region,
            "item-title": "title",
            "item-value": "value",
            density: "compact",
            "hide-details": "",
            variant: "outlined",
            "bg-color": "background"
          }, null, 8, ["items", "model-value", "disabled"]),
          _createVNode(_component_v_select, {
            items: __props.timeRanges,
            modelValue: selectedTimeRange.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedTimeRange).value = $event)),
            label: "Time Range",
            disabled: __props.disabled?.timeRange,
            density: "compact",
            "hide-details": "",
            variant: "outlined",
            "return-object": "",
            "bg-color": "background"
          }, {
            item: _withCtx(({ index, props }) => [
              _createVNode(_component_v_list_item, _normalizeProps(_guardReactiveProps({ ...props, title: props.value.name })), {
                title: _withCtx(() => [...(_cache[3] || (_cache[3] = []))]),
                default: _withCtx(() => [
                  _createVNode(_component_v_list_item_title, null, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(__props.timeRanges[index].name), 1)
                    ]),
                    _: 2
                  }, 1024),
                  (__props.timeRanges[index].name === 'Displayed Day')
                    ? (_openBlock(), _createBlock(_component_v_list_item_subtitle, { key: 0 }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(new Date((__props.timeRanges[index].range as MillisecondRange).start).toDateString()), 1)
                        ]),
                        _: 2
                      }, 1024))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1040)
            ]),
            selection: _withCtx(() => [
              _createTextVNode(_toDisplayString(selectedTimeRange.value?.name || 'Select Time Range'), 1)
            ]),
            _: 1
          }, 8, ["items", "modelValue", "disabled"]),
          _createVNode(_component_v_select, {
            items: availableMolecules.value.map(m => ({ title: m.title, value: m.key })),
            "model-value": draftUserDataset.value.molecule,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (setDraftSelectionMolecule($event))),
            label: "Molecule",
            disabled: __props.disabled?.molecule,
            "item-title": "title",
            "item-value": "value",
            density: "compact",
            "hide-details": "",
            variant: "outlined",
            "bg-color": "background"
          }, null, 8, ["items", "model-value", "disabled"]),
          _createVNode(_component_v_progress_linear, {
            "model-value": creationProgress.value.percent,
            height: "6",
            color: "primary",
            rounded: ""
          }, null, 8, ["model-value"]),
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_component_v_btn, {
              color: "primary",
              disabled: creationProgress.value.count < 3,
              onClick: composeSelection,
              size: "small",
              loading: loading.value
            }, {
              loader: _withCtx(() => [
                _createVNode(_component_v_progress_linear, { indeterminate: "" })
              ]),
              default: _withCtx(() => [
                _cache[4] || (_cache[4] = _createTextVNode(" Access Data ", -1))
              ]),
              _: 1
            }, 8, ["disabled", "loading"]),
            _createVNode(_component_v_btn, {
              color: "secondary",
              variant: "tonal",
              size: "small",
              onClick: reset
            }, {
              default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                _createTextVNode(" Reset ", -1)
              ]))]),
              _: 1
            })
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})